import InquiryPlate from '@/common/components/base/inquiryPlate/InquiryPlate';
import MainLayout from '@/common/components/layouts/MainLayout';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian';
import ImagesFabMainModal from '@/modules/adjusterReportModules/adjusterReport/components/imagesFab/ImagesFabMainModal';
import Stepper from '@/modules/adjusterReportModules/IncidentParticipants/components/common/Stepper';
import { VehicleInformationStepperData } from '@/modules/adjusterReportModules/IncidentParticipants/data/vehicleInformationStepper.data';
import { getVehicleInformationOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVehicleInformation/getVehicleInformationOption';
import { getVehicleInitialInformationOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVehicleInitialInformation/getVehicleInitialInformationOption';
import { PatchVehicleInformationAfterInquiryOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchVehicleInformationAfterInquiry/patchVehicleInformationAfterInquiryOption';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PropsWithChildren } from 'react';

import { useLocation, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box } from 'styled-system/jsx';

export default ({ children }: PropsWithChildren) => {
  return (
    <MainLayout
      title={'بیمه گذار / مشخصات وسیله‌نقلیه'}
      header="closeHeader"
      hasBottomPadding
    >
      <InitialVehicleInformation>{children}</InitialVehicleInformation>
    </MainLayout>
  );
};
const InitialVehicleInformation = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient();
  const { id, damagedId } = useParams();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const lastSegment = pathSegments[pathSegments.length - 1];

  const { data: vehicleInformationData } = useQuery(
    getVehicleInformationOption({
      type: 'initial',
      damaged_id: +damagedId!,
      id: +id!,
    }),
  );

  const { mutate: inquiryMutation } = useMutation(
    PatchVehicleInformationAfterInquiryOption(),
  );

  const isMotorCycle =
    vehicleInformationData?.vehicle_type?.name === 'motorcycle';

  return (
    <>
      <Box width="100%" pt={lastSegment !== 'part-two' ? '19px' : '0px'}>
        <Stepper steps={VehicleInformationStepperData} width="60%" />
      </Box>
      <Box
        className={css({
          paddingX: '16px',
          paddingTop:
            lastSegment !== 'part-two' && !isMotorCycle ? '24px' : '0px',
          mt: '24px',
        })}
      >
        {lastSegment !== 'part-two' && !isMotorCycle ? (
          <InquiryPlate
            fetchedResultHandler={(fetchedData) => {
              inquiryMutation(
                {
                  type: 'initial',
                  id: +id!,
                  damaged_id: +damagedId!,
                  data: {
                    vehicle_chassis:
                      (fetchedData?.vehicle_chassis as string) ?? null,
                    vehicle_color:
                      (fetchedData?.vehicle_color?.id as number) ?? null,
                    vehicle_plate_design:
                      fetchedData?.vehicle_plate_design ?? null,
                    vehicle_plate_type:
                      fetchedData?.vehicle_plate_type?.id ?? null,
                    vehicle_plate_sample: null,
                    vehicle_plate: `${fetchedData?.vehicle_plate_left}${fetchedData?.vehicle_plate_letter?.letter}${fetchedData?.vehicle_plate_right}${fetchedData?.vehicle_plate_iran}`,
                    vehicle_type:
                      (fetchedData?.vehicle_type?.id as number) ?? null,
                    vehicle_use:
                      (fetchedData?.vehicle_use?.id as number) ?? null,
                    fault_percentage: 0,
                    vehicle_engine:
                      (fetchedData?.vehicle_engine as string) ?? null,
                    vehicle_manufacturing_year:
                      (fetchedData?.vehicle_manufacturing_year as string) ??
                      null,
                    vehicle_model:
                      (fetchedData?.vehicle_model?.id as number) ?? null,
                    vehicle_price: null,
                    vehicle_vin: (fetchedData?.vehicle_vin as string) ?? null,
                    property_insurance: {
                      insurance:
                        fetchedData?.property_insurance?.insurance?.id ?? null,
                      insurance_number:
                        fetchedData?.property_insurance?.insurance_number ||
                        null,
                      insurance_start_date:
                        convertPersianToGregorian(
                          fetchedData?.property_insurance
                            ?.insurance_start_date ?? '',
                        ) || null,
                      insurance_expiry_date:
                        convertPersianToGregorian(
                          fetchedData?.property_insurance
                            ?.insurance_expiry_date ?? '',
                        ) || null,
                      insurance_expiry_date_last_year: null,
                    },
                  },
                },
                {
                  onSuccess: () => {
                    return queryClient.invalidateQueries({
                      queryKey: getVehicleInitialInformationOption({
                        id: +id!,
                      }).queryKey,
                    });
                  },
                },
              );
            }}
            fileId={id!}
          />
        ) : null}
      </Box>
      <Box
        w="100%"
        mx="auto"
        mt={40}
        mb={24}
        border="1px solid"
        borderColor="gray-2"
      />
      <Box width="100%">{children}</Box>
      <ImagesFabMainModal state="initial" />
    </>
  );
};
